import axiosInstance from "./axiosInstance";

export const checkEcu = async ({ file, brand, ecu }) => {
  const formData = new FormData();
  formData.append("file", file);
  if (brand && brand.trim()) formData.append("brand", brand.trim());
  if (ecu && ecu.trim()) formData.append("ecu", ecu.trim());

  const response = await axiosInstance.post("/ecu/check", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export const patchEcu = async ({
  file,
  brand,
  ecu,
  swNumber,
  modules = [],
  tuningIds = [],
  dtcPairs = [],
}) => {
  const formData = new FormData();
  formData.append("file", file);
  if (brand && brand.trim()) formData.append("brand", brand.trim());
  if (ecu && ecu.trim()) formData.append("ecu", ecu.trim());
  if (swNumber && swNumber.trim()) formData.append("sw_number", swNumber.trim());

  modules.forEach((name) => formData.append("modules", name));
  tuningIds.forEach((id) => formData.append("tuning_id", id));
  dtcPairs.forEach((pair) => {
    formData.append("dtcs", pair.code1 ?? "");
    formData.append("dtcs2", pair.code2 ?? "");
  });

  const response = await axiosInstance.post("/ecu/patch", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    responseType: "blob",
  });

  let fileName = "patched.bin";
  const disposition = response.headers["content-disposition"];
  if (disposition) {
    const match = disposition.match(/filename="?([^"]+)"?/);
    if (match && match.length > 1) fileName = match[1];
  }

  return {
    blob: new Blob([response.data]),
    fileName,
    charged: response.headers["x-credits-charged"],
    balance: response.headers["x-credits-balance"],
  };
};

export const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName || "patched.bin");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const DTC1_KEYS = ["dtcCode", "dtc_code", "dtc1", "DTC1", "code", "Code"];
const DTC2_KEYS = ["dtcCode2", "dtc_code2", "dtc2", "DTC2"];
const DTC_DESC_KEYS = [
  "dtcInfo",
  "dtc_info",
  "description",
  "dtcDescription",
  "desc",
  "text",
  "name",
  "fault",
];
const DTC_STATUS_KEYS = ["dtcStatus", "dtc_status", "status"];

// /ecu/check states what is still live in the uploaded file: every DTC row
// carries a `dtcStatus` and every identification.solutions entry a `status`,
// both "ON" (still present) or "OFF" (already removed). "OFF" is a fact about
// the file, not a customer choice — the switch has to show it, and there is
// nothing left to patch or charge for.
const isOffStatus = (status) => String(status || "").trim().toUpperCase() === "OFF";

// The engine writes "N/A" when it has no text for a code; an empty cell says
// the same thing without filling the column with noise.
const describe = (value) => (value.trim().toUpperCase() === "N/A" ? "" : value);

const pickCell = (row, keys) => {
  for (const key of keys) {
    if (row[key] != null && String(row[key]).trim() !== "") {
      return String(row[key]);
    }
  }
  return "";
};

export const extractDtcPairs = (dtcTable) => {
  if (!Array.isArray(dtcTable)) return [];
  const pairs = [];
  for (const row of dtcTable) {
    if (!row || typeof row !== "object") continue;
    const c1 = pickCell(row, DTC1_KEYS);
    const c2 = pickCell(row, DTC2_KEYS);
    const p1 = c1 ? c1.trim().split(/\s+/) : [];
    const p2 = c2 ? c2.trim().split(/\s+/) : [];
    const count = Math.max(p1.length, p2.length);
    for (let i = 0; i < count; i++) {
      const code1 = p1[i] ?? "";
      const code2 = p2[i] ?? "";
      if (code1 === "" && code2 === "") continue;
      pairs.push({ code1, code2 });
    }
  }
  return pairs;
};

// Splits one display row into {code1,code2} pairs (a cell can pack several
// whitespace-separated codes). Used to send only the DTC rows toggled off.
export const dtcRowToPairs = (row) => {
  if (!row) return [];
  const p1 = row.code1 ? String(row.code1).trim().split(/\s+/) : [];
  const p2 = row.code2 ? String(row.code2).trim().split(/\s+/) : [];
  const count = Math.max(p1.length, p2.length);
  const pairs = [];
  for (let i = 0; i < count; i++) {
    const code1 = p1[i] ?? "";
    const code2 = p2[i] ?? "";
    if (code1 === "" && code2 === "") continue;
    pairs.push({ code1, code2 });
  }
  return pairs;
};

export const extractDtcRows = (dtcTable) => {
  if (!Array.isArray(dtcTable)) return [];
  const rows = [];
  for (const row of dtcTable) {
    if (!row || typeof row !== "object") continue;
    const code1 = pickCell(row, DTC1_KEYS);
    const code2 = pickCell(row, DTC2_KEYS);
    const description = describe(pickCell(row, DTC_DESC_KEYS));
    if (!code1 && !code2 && !description) continue;
    rows.push({ code1, code2, description, alreadyOff: isOffStatus(pickCell(row, DTC_STATUS_KEYS)) });
  }
  return rows;
};

/**
 * The names the check reports as already off, from identification.solutions or
 * identification.tunings ([{ name, status, ... }]). A name missing from the
 * list is left alone: the check said nothing about it, so it is treated as on.
 */
export const extractAlreadyOff = (list) => {
  const off = new Set();
  if (!Array.isArray(list)) return off;
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const name = String(item.name ?? "").trim();
    if (name && isOffStatus(item.status)) off.add(name);
  }
  return off;
};

// Names of the solutions the check reports ON for this file — the ones that apply to it (what a
// bug report about this file can point at).
export const extractOnSolutions = (list) => {
  const on = [];
  if (!Array.isArray(list)) return on;
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const name = String(item.name ?? "").trim();
    if (name && String(item.status || "").trim().toUpperCase() === "ON" && !on.includes(name)) {
      on.push(name);
    }
  }
  return on;
};

export const hasDtc2Column = (rows) => rows.some((row) => row.code2 && row.code2 !== "");

export const extractApiError = async (err, fallback) => {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return parsed.error || parsed.message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.error || data?.message || err?.message || fallback;
};

// --- ECU disambiguation ------------------------------------------------------
// /ecu/check answers 409 when a file matches several equally-specific ECUs and
// one has to be chosen:
//
//   {
//     "requiresEcuSelection": true,
//     "message": "Multiple ECUs match this file. Select one and resubmit …",
//     "ecuCandidates": [ { "brand": "Suzuki", "ecu": "EDC16C3" },
//                        { "brand": "PSA",    "ecu": "EDC16C3" } ]
//   }
//
// `ecuCandidates` is read first. The wider search that follows it is a
// fallback for a body that carries the pairs under another key or nests them
// inside an envelope — an empty result means the body named none and the
// customer has to type the pair in by hand.

/** True for the selection payload above, whatever status code carried it. */
export const isEcuSelectionPayload = (payload) =>
  Boolean(payload) &&
  typeof payload === "object" &&
  (payload.requiresEcuSelection === true || payload.requires_ecu_selection === true) &&
  Array.isArray(payload.ecuCandidates ?? payload.ecu_candidates);

const CANDIDATE_BRAND_KEYS = [
  "brand",
  "systemManufacturer",
  "system_manufacturer",
  "manufacturer",
  "make",
];
const CANDIDATE_ECU_KEYS = ["ecu", "ecuId", "ecu_id", "ecuName", "ecu_name", "name"];

const pickKey = (record, keys) => {
  for (const key of keys) {
    const value = record[key];
    if (value != null && String(value).trim() !== "") return String(value).trim();
  }
  return "";
};

export const extractEcuCandidates = (payload) => {
  const out = [];
  const seen = new Set();

  const add = (brand, ecu) => {
    if (!brand && !ecu) return;
    const key = `${brand}||${ecu}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ brand, ecu });
  };

  const visit = (node, key, depth) => {
    if (!node || depth > 5) return;

    if (Array.isArray(node)) {
      const nameOf = String(key || "").toLowerCase();
      for (const item of node) {
        if (item && typeof item === "object") {
          add(pickKey(item, CANDIDATE_BRAND_KEYS), pickKey(item, CANDIDATE_ECU_KEYS));
        } else if (typeof item === "string" && item.trim()) {
          // A bare list of strings only says what it holds through its key.
          if (nameOf.includes("brand") || nameOf.includes("manufacturer")) add(item.trim(), "");
          else if (nameOf.includes("ecu")) add("", item.trim());
        }
      }
      return;
    }

    if (typeof node === "object") {
      for (const childKey of Object.keys(node)) visit(node[childKey], childKey, depth + 1);
    }
  };

  const declared = payload?.ecuCandidates ?? payload?.ecu_candidates;
  if (Array.isArray(declared)) {
    for (const item of declared) {
      if (item && typeof item === "object") {
        add(pickKey(item, CANDIDATE_BRAND_KEYS), pickKey(item, CANDIDATE_ECU_KEYS));
      }
    }
    if (out.length > 0) return out;
  }

  visit(payload, "", 0);
  return out;
};
